/**
 * CSV and JSON export utilities for Secret Santa results
 */

import { AppState, SecretSantaResult } from "./types.ts";

/**
 * Escapes a CSV field value according to RFC 4180
 * - Wraps in quotes if contains comma, quote, or newline
 * - Doubles any existing quotes
 */
export function escapeCsvField(value: string): string {
  // Check if field needs escaping (contains comma, quote, or newline)
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    // Double any existing quotes and wrap in quotes
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Generates CSV content from Secret Santa results
 * Format: Giver,Recipient[,metadata fields...]
 */
export function generateCsv(state: AppState, result: SecretSantaResult): string {
  // Build header row
  const headerColumns = ["Giver", "Recipient"];
  for (const fieldName of state.metadataFields) {
    headerColumns.push(escapeCsvField(fieldName));
  }
  const lines: string[] = [headerColumns.join(",")];

  // Data rows
  for (let i = 0; i < result.length; i++) {
    const columns = [
      escapeCsvField(state.names[i]),
      escapeCsvField(state.names[result[i]]),
    ];

    // Add metadata values for this person
    for (let fieldIndex = 0; fieldIndex < state.metadataFields.length; fieldIndex++) {
      const value = state.metadata[i]?.[fieldIndex] || "";
      columns.push(escapeCsvField(value));
    }

    lines.push(columns.join(","));
  }

  // Join with newlines
  return lines.join("\n");
}

/**
 * Triggers a CSV file download in the browser
 */
export function downloadCsv(state: AppState, result: SecretSantaResult): void {
  const csv = generateCsv(state, result);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  // Create temporary link and trigger download
  const link = document.createElement("a");
  link.href = url;
  link.download = `secret-santa-${Date.now()}.csv`;
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL
  URL.revokeObjectURL(url);
}

/**
 * Generates JSON content from Secret Santa results
 */
export function generateJson(state: AppState, result: SecretSantaResult): string {
  const assignments = result.map((recipientIndex, giverIndex) => {
    const assignment: Record<string, string> = {
      giver: state.names[giverIndex],
      recipient: state.names[recipientIndex],
    };

    // Add metadata fields if they exist
    for (let fieldIndex = 0; fieldIndex < state.metadataFields.length; fieldIndex++) {
      const fieldName = state.metadataFields[fieldIndex];
      const giverValue = state.metadata[giverIndex]?.[fieldIndex] || "";
      assignment[`giver_${fieldName}`] = giverValue;
    }

    return assignment;
  });

  return JSON.stringify(assignments, null, 2);
}

/**
 * Triggers a JSON file download in the browser
 */
export function downloadJson(state: AppState, result: SecretSantaResult): void {
  const json = generateJson(state, result);
  const blob = new Blob([json], { type: "application/json;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  // Create temporary link and trigger download
  const link = document.createElement("a");
  link.href = url;
  link.download = `secret-santa-${Date.now()}.json`;
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL
  URL.revokeObjectURL(url);
}
